import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Badge, PageHeader, Modal, Input, Select, Alert } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const S_COLOR = { draft:'gray', scheduled:'info', active:'success', paused:'warning', completed:'gray' };
// ── FIXED SENDING SETTINGS ─────────────────────────────────────────────────
// Timezone, email interval, and sending days are fixed platform-wide and are
// no longer configurable from the frontend.
const FIXED_TIMEZONE = 'Asia/Kolkata';
const FIXED_SENDING_INTERVAL = 2; // minutes
const FIXED_SENDING_DAYS = 'mon,tue,wed,thu,fri';
const DAYS = [['mon','Mon'],['tue','Tue'],['wed','Wed'],['thu','Thu'],['fri','Fri'],['sat','Sat'],['sun','Sun']];
const DAYS_LABEL = FIXED_SENDING_DAYS.split(',').map(v => DAYS.find(([val]) => val === v)?.[1]).filter(Boolean).join(', ');

const EMPTY_FORM = { name:'', type:'outreach', subject:'', body:'', dailyLimit:50, sendingInterval:FIXED_SENDING_INTERVAL, templateId:'', leadListIds:[], fromAccountIds:[], sendingDays:FIXED_SENDING_DAYS, sendingTimezone:FIXED_TIMEZONE, scheduledAt:'', notificationEmail:'', abVersionA:{ subject:'', body:'' }, abVersionB:{ subject:'', body:'' }, abSplitPercent:50, abWinnerMetric:'open_rate', abWinnerAfterHours:4 };

// ── TIMEZONE FIX ─────────────────────────────────────────────────────────────
// Convert a local datetime-local string (e.g. "2026-04-15T09:00") interpreted
// in the campaign's sendingTimezone into a proper UTC ISO string for the backend.
// Without this, the browser treats the value as local browser time, not the
// campaign's selected timezone, causing 5:30h offset for IST users.
const toUTCString = (localDateStr, timezone) => {
  if (!localDateStr) return null;
  try {
    // Parse the local datetime as if it's in the selected timezone
    // We do this by formatting a reference date in that timezone and comparing offsets
    const [datePart, timePart] = localDateStr.split('T');
    const [year, month, day]   = datePart.split('-').map(Number);
    const [hour, minute]       = timePart.split(':').map(Number);

    // Use Intl to find the UTC offset for this timezone at this moment
    const testDate  = new Date(Date.UTC(year, month - 1, day, hour, minute));
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });

    // Get what this UTC time looks like in the target timezone
    const parts = formatter.formatToParts(testDate);
    const tzHour   = parseInt(parts.find(p => p.type === 'hour').value,   10);
    const tzMinute = parseInt(parts.find(p => p.type === 'minute').value, 10);
    const tzDay    = parseInt(parts.find(p => p.type === 'day').value,    10);
    const tzMonth  = parseInt(parts.find(p => p.type === 'month').value,  10);
    const tzYear   = parseInt(parts.find(p => p.type === 'year').value,   10);

    // Calculate offset: diff between what we wanted (local input) and what UTC gave us
    const wantedMs = Date.UTC(year, month - 1, day, hour, minute);
    const gotMs    = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute);
    const offsetMs = wantedMs - gotMs;

    // Apply offset to get true UTC
    return new Date(testDate.getTime() + offsetMs).toISOString();
  } catch (e) {
    // Fallback: treat as local browser time
    return new Date(localDateStr).toISOString();
  }
};

// Convert a UTC ISO string back to datetime-local string in the campaign's timezone
// Used when editing an existing campaign to show the correct local time
const fromUTCToLocal = (utcStr, timezone) => {
  if (!utcStr) return '';
  try {
    const date = new Date(utcStr);
    const formatter = new Intl.DateTimeFormat('sv-SE', { // sv-SE gives YYYY-MM-DD HH:MM format
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    return formatter.format(date).replace(' ', 'T');
  } catch {
    return new Date(utcStr).toISOString().slice(0, 16);
  }
};

export default function Campaigns() {
  const { user } = useAuthStore();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists]         = useState([]);
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [step, setStep]           = useState(1);
  const [showTest, setShowTest]   = useState(null);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [testLoading, setTestLoading] = useState(false);
  const [showSpam, setShowSpam]   = useState(null);
  const [spamResult, setSpamResult] = useState(null);
  const [spamLoading, setSpamLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const [c,t,l,a] = await Promise.allSettled([api.get('/campaigns'),api.get('/templates'),api.get('/leads/lists'),api.get('/email-accounts')]);
      if (c.status==='fulfilled') {
        const d = c.value.data;
        setCampaigns(d?.data || d?.campaigns || (Array.isArray(d) ? d : []));
      } else {
        const msg = c.reason?.response?.data?.message || c.reason?.message || 'Failed to load campaigns';
        setFetchError(msg);
      }
      if (t.status==='fulfilled') setTemplates(t.value.data?.data || t.value.data?.templates || []);
      if (l.status==='fulfilled') setLists(l.value.data?.data || l.value.data?.lists || []);
      if (a.status==='fulfilled') setAccounts(a.value.data?.data || a.value.data?.accounts || []);
    } catch (e) {
      setFetchError(e.message || 'Unexpected error loading campaigns');
    } finally { setLoading(false); }
  };

  const openCreate = () => { setEditId(null); setForm(EMPTY_FORM); setStep(1); setShowModal(true); };
  const openEdit   = (c) => {
    setEditId(c.id);
    // Timezone, sending interval, and sending days are fixed platform-wide,
    // regardless of what an older campaign record may have stored.
    const tz = FIXED_TIMEZONE;
    setForm({
      name: c.name, type: c.type, subject: c.subject||'', body: c.body||'',
      dailyLimit: c.dailyLimit||50, sendingInterval: FIXED_SENDING_INTERVAL,
      templateId: '',
      leadListIds: c.leadListIds||[], fromAccountIds: c.fromAccountIds||[],
      sendingDays: FIXED_SENDING_DAYS,
      sendingTimezone: tz,
      // ── TIMEZONE FIX: convert stored UTC back to local time in the campaign's timezone
      scheduledAt: c.scheduledAt ? fromUTCToLocal(c.scheduledAt, tz) : '',
      notificationEmail: c.notificationEmail||'',
      abVersionA: c.abVersionA||{ subject:'', body:'' },
      abVersionB: c.abVersionB||{ subject:'', body:'' },
      abSplitPercent: c.abSplitPercent||50,
      abWinnerMetric: c.abWinnerMetric||'open_rate',
      abWinnerAfterHours: c.abWinnerAfterHours||4,
    });
    setStep(1); setShowModal(true);
  };

  const selectTemplate = (id) => {
    const tpl = templates.find(t => t.id === id);
    if (tpl) setForm(p => ({ ...p, templateId:id, subject:tpl.subject||p.subject, body:tpl.body||p.body }));
    else setForm(p => ({ ...p, templateId:'' }));
  };
  const toggleList    = (id) => setForm(p => ({ ...p, leadListIds: p.leadListIds.includes(id) ? p.leadListIds.filter(x=>x!==id) : [...p.leadListIds,id] }));
  const toggleAccount = (id) => setForm(p => ({ ...p, fromAccountIds: p.fromAccountIds.includes(id) ? p.fromAccountIds.filter(x=>x!==id) : [...p.fromAccountIds,id] }));

  const saveCampaign = async () => {
    if (!form.name)    return toast.error('Campaign name required');
    if (!form.subject) return toast.error('Subject required');
    if (!form.body)    return toast.error('Email body required');
    if (!editId && form.leadListIds.length===0)    return toast.error('Select at least one contact list');
    if (!editId && form.fromAccountIds.length===0) return toast.error('Select a sending account');
    setSaving(true);
    try {
      const { templateId, ...rest } = form;

      // ── TIMEZONE FIX: convert scheduledAt from local campaign timezone to UTC
      const scheduledAtUTC = form.scheduledAt
        ? toUTCString(form.scheduledAt, form.sendingTimezone)
        : null;

      const payload = { ...rest, scheduledAt: scheduledAtUTC };

      if (editId) { await api.put(`/campaigns/${editId}`, payload); toast.success('Campaign updated!'); }
      else        { await api.post('/campaigns', { ...payload, templateId }); toast.success('Campaign created! It will start automatically at the scheduled time.'); }
      setShowModal(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/campaigns/${id}`, { status }); fetchAll(); toast.success(`Campaign ${status}`); }
    catch (e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const campaignAction = async (id, action) => {
    try {
      await api.post(`/campaigns/${id}/action`, { action });
      fetchAll();
      toast.success(`Campaign ${action === 'start' ? 'resumed' : action === 'pause' ? 'paused' : 'stopped'}`);
    } catch (e) { toast.error(e.response?.data?.message || 'Action failed'); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await api.delete(`/campaigns/${id}`); fetchAll(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const duplicateCampaign = async (id) => {
    try { await api.post(`/campaigns/${id}/duplicate`); fetchAll(); toast.success('Campaign duplicated!'); } catch { toast.error('Failed'); }
  };

  const sendTestEmail = async (id) => {
    if (!testEmail) return toast.error('Enter a recipient email');
    setTestLoading(true);
    try { await api.post(`/campaigns/${id}/test`, { to: testEmail }); toast.success(`Test email sent to ${testEmail}`); setShowTest(null); }
    catch (e) { toast.error(e.response?.data?.message||'Failed'); } finally { setTestLoading(false); }
  };

  const checkSpam = async (c) => {
    setShowSpam(c); setSpamResult(null); setSpamLoading(true);
    try { const r = await api.post(`/campaigns/${c.id}/spam-check`, { subject:c.subject, body:c.body }); setSpamResult(r.data); }
    catch (e) { toast.error('Spam check failed'); } finally { setSpamLoading(false); }
  };

  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));
  const sendsLeft = user?.emailSendsLimit===-1?'∞':Math.max(0,(user?.emailSendsLimit||0)-(user?.emailSendsUsed||0));
  const sendsPct  = user?.emailSendsLimit>0?Math.min(100,((user?.emailSendsUsed||0)/user.emailSendsLimit)*100):0;
  const canNext   = () => { if(step===1) return form.name.trim()&&form.type; if(step===2) return form.subject.trim()&&form.body.trim(); if(step===3) return form.leadListIds.length>0&&form.fromAccountIds.length>0; return true; };
  const STEPS = ['Basics','Email Content','Audience','Settings'];

  // Preview of scheduled time in the campaign's timezone
  const scheduledPreview = form.scheduledAt ? (() => {
    try {
      const utc = toUTCString(form.scheduledAt, form.sendingTimezone);
      return new Date(utc).toLocaleString('en-US', { timeZone: form.sendingTimezone, dateStyle:'medium', timeStyle:'short' }) + ` (${form.sendingTimezone})`;
    } catch { return form.scheduledAt; }
  })() : null;

  return (
    <div>
      <PageHeader title="Campaigns" subtitle={`${campaigns.length} campaigns · ${sendsLeft} sends remaining`} action={<Button onClick={openCreate}>+ New Campaign</Button>} />

      {user?.emailSendsLimit!==-1 && (
        <Card style={{ marginBottom:20, padding:'14px 20px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
            <span style={{ fontSize:13, color:'#888' }}>📤 Email Sends Used</span>
            <span style={{ fontSize:13, fontWeight:700, color:sendsPct>=90?'#E74C3C':sendsPct>=70?'#F39C12':'#2ECC71' }}>{user?.emailSendsUsed?.toLocaleString()} / {user?.emailSendsLimit?.toLocaleString()}</span>
          </div>
          <div style={{ background:'#1a1a1a', borderRadius:6, height:8 }}>
            <div style={{ width:`${sendsPct}%`, height:'100%', borderRadius:6, background:sendsPct>=90?'#E74C3C':sendsPct>=70?'#F39C12':'#2ECC71', transition:'width 0.5s' }} />
          </div>
          {sendsPct>=90 && <Alert type="danger" style={{ marginTop:10, padding:'8px 12px' }}>Sends almost exhausted. <strong>Upgrade your plan.</strong></Alert>}
        </Card>
      )}

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : fetchError ? (
        <Card style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
          <h3 style={{ color:'#E74C3C', marginBottom:8 }}>Could not load campaigns</h3>
          <p style={{ color:'#777', fontSize:13, marginBottom:20 }}>{fetchError}</p>
          <Button onClick={fetchAll}>🔄 Retry</Button>
        </Card>
      ) : (
        campaigns.length===0 ? (
          <Card style={{ textAlign:'center', padding:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>📧</div>
            <h3 style={{ color:'#ccc', marginBottom:8 }}>No campaigns yet</h3>
            <p style={{ color:'#555', fontSize:14, marginBottom:24 }}>Create your first campaign to start reaching your leads.</p>
            <Button onClick={openCreate}>+ Create Campaign</Button>
          </Card>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {campaigns.map(c => (
              <Card key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                    <span style={{ fontWeight:700, fontSize:15 }}>{c.name}</span>
                    <Badge color={S_COLOR[c.status]||'gray'}>{c.status}</Badge>
                    <Badge color="gray">{c.type}</Badge>
                    {c.status==='scheduled' && c.scheduledAt && (
                      <span style={{ fontSize:11, color:'#3498DB' }}>
                        🕐 {new Date(c.scheduledAt).toLocaleString('en-US', { timeZone: FIXED_TIMEZONE, dateStyle:'medium', timeStyle:'short' })} ({FIXED_TIMEZONE})
                      </span>
                    )}
                    {c.isAbTest && !c.abWinner && <Badge color="info">🧪 A/B Running</Badge>}
                    {c.isAbTest && c.abWinner && <Badge color="success">🏆 Winner: Version {c.abWinner.toUpperCase()}</Badge>}
                  </div>
                  <div style={{ display:'flex', gap:20, fontSize:12, color:'#555' }}>
                    <span>📤 {c.statsSent||0} sent</span><span>👀 {c.statsOpened||0} opened</span>
                    <span>🖱️ {c.statsClicked||0} clicked</span><span>💬 {c.statsReplied||0} replied</span><span>⚠️ {c.statsBounced||0} bounced</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  <Link to={`/client/campaigns/${c.id}/report`}><Button size="sm" variant="ghost">📊</Button></Link>
                  <Button size="sm" variant="ghost" title="Preview email" onClick={() => setShowPreview(c)}>👁</Button>
                  <Button size="sm" variant="ghost" title="Send test email" onClick={() => { setShowTest(c); setTestEmail(user?.email||''); }}>📨</Button>
                  <Button size="sm" variant="ghost" title="Check spam score" onClick={() => checkSpam(c)}>🔍</Button>
                  <Button size="sm" variant="ghost" title="Duplicate" onClick={() => duplicateCampaign(c.id)}>📋</Button>
                  <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(c)}>✏️</Button>
                  {c.status==='paused' && <Button size="sm" style={{ background:'rgba(46,204,113,0.1)', color:'#2ECC71', border:'1px solid rgba(46,204,113,0.3)' }} onClick={()=>campaignAction(c.id,'start')}>▶ Resume</Button>}
                  {(c.status==='active'||c.status==='scheduled') && <Button size="sm" style={{ background:'rgba(243,156,18,0.1)', color:'#F39C12', border:'1px solid rgba(243,156,18,0.3)' }} onClick={()=>campaignAction(c.id,'pause')}>⏸ Pause</Button>}
                  {(c.status==='active'||c.status==='paused') && <Button size="sm" style={{ background:'rgba(231,76,60,0.1)', color:'#E74C3C', border:'1px solid rgba(231,76,60,0.3)' }} onClick={()=>campaignAction(c.id,'stop')}>⏹ Stop</Button>}
                  <Button size="sm" variant="danger" onClick={() => deleteCampaign(c.id)}>🗑</Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {/* ── CREATE/EDIT MODAL ── */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title={editId ? 'Edit Campaign' : 'Create Campaign'} size="lg">
        <div style={{ display:'flex', gap:0, marginBottom:24 }}>
          {STEPS.map((s,i) => (
            <div key={s} style={{ flex:1, textAlign:'center', cursor:'pointer' }} onClick={() => i<step&&setStep(i+1)}>
              <div style={{ height:4, borderRadius:2, background:i+1<=step?'#2ECC71':'#222', marginBottom:5, transition:'background 0.3s' }} />
              <div style={{ fontSize:11, fontWeight:i+1===step?700:400, color:i+1===step?'#2ECC71':i+1<step?'#555':'#333' }}>{i+1<step?'✓ ':''}{s}</div>
            </div>
          ))}
        </div>

        {(step===1 || editId) && (
          <div>
            <Input label="Campaign Name *" placeholder="e.g. Q1 Cold Outreach" value={form.name} onChange={e=>f('name',e.target.value)} />
            <Select label="Type" value={form.type} onChange={e=>f('type',e.target.value)}>
              <option value="outreach">Cold Outreach</option><option value="sequence">Sequence</option><option value="broadcast">Broadcast</option><option value="ab_test">A/B Test</option>
            </Select>
            <Input label="Daily Sending Limit" type="number" value={form.dailyLimit} onChange={e=>f('dailyLimit',parseInt(e.target.value)||50)} />
          </div>
        )}

        {(step===2 || editId) && (
          <div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:10 }}>📝 Template <span style={{ color:'#555', fontWeight:400 }}>(optional — auto-fills subject & body)</span></label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:8, maxHeight:160, overflowY:'auto' }}>
                <div onClick={()=>setForm(p=>({...p,templateId:''}))} style={{ border:`2px solid ${!form.templateId?'#2ECC71':'rgba(46,204,113,0.12)'}`, borderRadius:10, padding:'10px 12px', cursor:'pointer', background:!form.templateId?'rgba(46,204,113,0.08)':'#1a1a1a' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:!form.templateId?'#2ECC71':'#555' }}>✏️ Write from scratch</div>
                </div>
                {templates.map(t => (
                  <div key={t.id} onClick={()=>selectTemplate(t.id)} style={{ border:`2px solid ${form.templateId===t.id?'#2ECC71':'rgba(46,204,113,0.12)'}`, borderRadius:10, padding:'10px 12px', cursor:'pointer', background:form.templateId===t.id?'rgba(46,204,113,0.08)':'#1a1a1a' }}>
                    <div style={{ fontSize:12, fontWeight:700, color:form.templateId===t.id?'#2ECC71':'#ccc', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.name}</div>
                    <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{t.category}</div>
                  </div>
                ))}
              </div>
            </div>
            <Input label="Subject *" placeholder="e.g. Quick question about {{company}}" value={form.subject} onChange={e=>f('subject',e.target.value)} hint="Variables: {{first_name}} {{company}} {{email}} {{job_title}}" />
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>Email Body *</label>
              <div style={{ display:'flex', gap:6, marginBottom:6, flexWrap:'wrap' }}>
                {[{l:'B',t:'Bold',w:['<b>','</b>']},{l:'I',t:'Italic',w:['<i>','</i>']},{l:'U',t:'Underline',w:['<u>','</u>']}].map(({l,t,w})=>(
                  <button key={l} title={t} onMouseDown={e=>{ e.preventDefault(); const ta=document.getElementById('cmp-body'); const s=ta.selectionStart,en=ta.selectionEnd; const sel=form.body.slice(s,en); const nv=form.body.slice(0,s)+w[0]+sel+w[1]+form.body.slice(en); f('body',nv); setTimeout(()=>{ta.focus();ta.setSelectionRange(s+w[0].length,en+w[0].length);},0); }} style={{ padding:'4px 10px', background:'#222', border:'1px solid rgba(46,204,113,0.2)', borderRadius:6, color:'#ccc', cursor:'pointer', fontWeight:l==='B'?700:400, fontStyle:l==='I'?'italic':'normal', textDecoration:l==='U'?'underline':'none', fontSize:13 }}>{l}</button>
                ))}
                <button title="Insert Hyperlink" onMouseDown={e=>{ e.preventDefault(); const ta=document.getElementById('cmp-body'); const s=ta.selectionStart,en=ta.selectionEnd; const sel=form.body.slice(s,en)||'click here'; const url=window.prompt('Enter URL:','https://'); if(!url)return; const link=`<a href="${url}">${sel}</a>`; f('body',form.body.slice(0,s)+link+form.body.slice(en)); setTimeout(()=>ta.focus(),0); }} style={{ padding:'4px 10px', background:'#222', border:'1px solid rgba(46,204,113,0.2)', borderRadius:6, color:'#2ECC71', cursor:'pointer', fontSize:13 }}>🔗 Link</button>
                <button title="Insert Button" onMouseDown={e=>{ e.preventDefault(); const ta=document.getElementById('cmp-body'); const s=ta.selectionStart; const url=window.prompt('Enter URL:','https://'); if(!url)return; const lbl=window.prompt('Button text:','Click Here'); if(!lbl)return; const btn=`<a href="${url}" style="display:inline-block;padding:10px 24px;background:#2ECC71;color:#fff;text-decoration:none;border-radius:6px;font-weight:bold;">${lbl}</a>`; f('body',form.body.slice(0,s)+btn+form.body.slice(s)); setTimeout(()=>ta.focus(),0); }} style={{ padding:'4px 10px', background:'#222', border:'1px solid rgba(46,204,113,0.2)', borderRadius:6, color:'#3498DB', cursor:'pointer', fontSize:13 }}>🔘 Button</button>
                <button title="Find & Replace URL" onMouseDown={e=>{ e.preventDefault(); const find=window.prompt('Find URL:','https://chat.msdiscoveries.com'); if(!find)return; const replace=window.prompt('Replace with:','https://msdiscoveries.com'); if(replace===null)return; const count=(form.body.match(new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g,'\\$&'),'g'))||[]).length; if(!count){alert('URL not found in body');return;} f('body',form.body.split(find).join(replace)); alert(`Replaced ${count} occurrence(s)`); }} style={{ padding:'4px 10px', background:'#222', border:'1px solid rgba(46,204,113,0.2)', borderRadius:6, color:'#F39C12', cursor:'pointer', fontSize:13 }}>🔄 Replace URL</button>
              </div>
              <textarea id="cmp-body" value={form.body} onChange={e=>f('body',e.target.value)} rows={10} placeholder={"Hi {{first_name}},\n\nYour message here...\n\nBest regards"} style={{ width:'100%', padding:'12px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none', resize:'vertical', fontFamily:'inherit', lineHeight:1.7 }} />
              <div style={{ fontSize:11, color:'#555', marginTop:4 }}>Select text then click toolbar to format &nbsp;|&nbsp; Variables: {'{{first_name}}'} {'{{company}}'} {'{{email}}'}</div>
              {form.body && form.body.includes('<') && (
                <div style={{ marginTop:10 }}>
                  <div style={{ fontSize:11, color:'#555', marginBottom:4 }}>👁 Preview (how it looks in email):</div>
                  <div style={{ background:'#fff', borderRadius:8, padding:'12px 16px', fontSize:14, lineHeight:1.8, color:'#222' }}
                    dangerouslySetInnerHTML={{ __html: form.body.replace(/\n/g,'<br>') }} />
                </div>
              )}
            </div>
            {form.type === 'ab_test' && (
              <div style={{ marginTop:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, padding:'10px 16px', background:'rgba(52,152,219,0.08)', border:'1px solid rgba(52,152,219,0.2)', borderRadius:10 }}>
                  <span style={{ fontSize:18 }}>🧪</span>
                  <div style={{ fontSize:13, fontWeight:700, color:'#3498DB' }}>A/B Test Mode — Write two versions to test which performs better</div>
                </div>
                <div style={{ background:'rgba(46,204,113,0.05)', border:'2px solid rgba(46,204,113,0.2)', borderRadius:12, padding:16, marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:'#2ECC71', marginBottom:12 }}>🅰 Version A</div>
                  <Input label="Subject A *" value={form.abVersionA?.subject||''} onChange={e=>f('abVersionA',{...form.abVersionA, subject:e.target.value})} />
                  <div style={{ marginBottom:8 }}>
                    <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>Body A *</label>
                    <textarea value={form.abVersionA?.body||''} onChange={e=>f('abVersionA',{...form.abVersionA, body:e.target.value})} rows={6} style={{ width:'100%', padding:'12px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none', resize:'vertical', fontFamily:'inherit', lineHeight:1.7 }} />
                  </div>
                </div>
                <div style={{ background:'rgba(52,152,219,0.05)', border:'2px solid rgba(52,152,219,0.2)', borderRadius:12, padding:16, marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:'#3498DB', marginBottom:12 }}>🅱 Version B</div>
                  <Input label="Subject B *" value={form.abVersionB?.subject||''} onChange={e=>f('abVersionB',{...form.abVersionB, subject:e.target.value})} />
                  <div style={{ marginBottom:8 }}>
                    <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>Body B *</label>
                    <textarea value={form.abVersionB?.body||''} onChange={e=>f('abVersionB',{...form.abVersionB, body:e.target.value})} rows={6} style={{ width:'100%', padding:'12px 14px', background:'#1a1a1a', border:'1px solid rgba(52,152,219,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none', resize:'vertical', fontFamily:'inherit', lineHeight:1.7 }} />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                  <div>
                    <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>Split % (Version A)</label>
                    <input type="number" min="10" max="90" value={form.abSplitPercent||50} onChange={e=>f('abSplitPercent',parseInt(e.target.value)||50)} style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none' }} />
                    <div style={{ fontSize:11, color:'#555', marginTop:3 }}>B gets {100-(form.abSplitPercent||50)}%</div>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>Winner Metric</label>
                    <select value={form.abWinnerMetric||'open_rate'} onChange={e=>f('abWinnerMetric',e.target.value)} style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none' }}>
                      <option value="open_rate">Open Rate</option>
                      <option value="click_rate">Click Rate</option>
                      <option value="reply_rate">Reply Rate</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>Pick Winner After</label>
                    <input type="number" min="1" max="72" value={form.abWinnerAfterHours||4} onChange={e=>f('abWinnerAfterHours',parseInt(e.target.value)||4)} style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none' }} />
                    <div style={{ fontSize:11, color:'#555', marginTop:3 }}>hours</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {(step===3 || editId) && (
          <div>
            <div style={{ marginBottom:24 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:10 }}>📋 Contact Lists * ({form.leadListIds.length} selected)</label>
              {lists.length===0 ? <Alert type="warning">No lists found. <Link to="/client/leads" style={{ color:'#F39C12', fontWeight:700 }}>Import leads first →</Link></Alert> : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {lists.map(l => {
                    const sel = form.leadListIds.includes(l.id);
                    return (
                      <div key={l.id} onClick={()=>toggleList(l.id)} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', background:sel?'rgba(46,204,113,0.08)':'#1a1a1a', border:`2px solid ${sel?'#2ECC71':'rgba(46,204,113,0.12)'}`, borderRadius:12, cursor:'pointer', transition:'all 0.15s' }}>
                        <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${sel?'#2ECC71':'#444'}`, background:sel?'#2ECC71':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{sel&&<span style={{ color:'#000', fontSize:13, fontWeight:900 }}>✓</span>}</div>
                        <div style={{ flex:1 }}><div style={{ fontSize:14, fontWeight:600, color:sel?'#fff':'#ccc' }}>📋 {l.name}</div><div style={{ fontSize:12, color:'#555', marginTop:2 }}>{l.count||0} contacts</div></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:10 }}>✉️ Sending Accounts * ({form.fromAccountIds.length} selected)</label>
              {accounts.length===0 ? <Alert type="warning">No accounts connected. <Link to="/client/email-accounts" style={{ color:'#F39C12', fontWeight:700 }}>Connect one →</Link></Alert> : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {accounts.map(a => {
                    const sel = form.fromAccountIds.includes(a.id);
                    return (
                      <div key={a.id} onClick={()=>toggleAccount(a.id)} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', background:sel?'rgba(46,204,113,0.08)':'#1a1a1a', border:`2px solid ${sel?'#2ECC71':'rgba(46,204,113,0.12)'}`, borderRadius:12, cursor:'pointer', transition:'all 0.15s' }}>
                        <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${sel?'#2ECC71':'#444'}`, background:sel?'#2ECC71':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{sel&&<span style={{ color:'#000', fontSize:13, fontWeight:900 }}>✓</span>}</div>
                        <div style={{ flex:1 }}><div style={{ fontSize:14, fontWeight:600, color:sel?'#fff':'#ccc' }}>{a.name||a.email}</div><div style={{ fontSize:12, color:'#555' }}>{a.email} · Score: {a.warmupScore}/100</div></div>
                        <Badge color={a.status==='active'?'success':'warning'}>{a.status}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {(step===4 || editId) && (
          <div>
            <Input label="Daily Limit" type="number" min="1" max="500" value={form.dailyLimit} onChange={e=>f('dailyLimit',parseInt(e.target.value))} />

            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>
                📅 Campaign Start Date <span style={{ color:'#555', fontWeight:400 }}>(optional — leave blank to start immediately)</span>
              </label>
              <input
                type="datetime-local"
                value={form.scheduledAt}
                onChange={e=>f('scheduledAt',e.target.value)}
                style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color: form.scheduledAt ? '#fff' : '#555', fontSize:13, outline:'none' }}
              />
              {!form.scheduledAt && (
                <div style={{ fontSize:11, color:'#3498DB', marginTop:5 }}>
                  💡 No date set — campaign will activate automatically within 5 minutes of creation.
                </div>
              )}
              {form.scheduledAt && scheduledPreview && (
                <div style={{ fontSize:11, color:'#2ECC71', marginTop:5 }}>
                  🕐 Campaign will automatically start on <strong>{scheduledPreview}</strong>
                </div>
              )}
              {form.scheduledAt && (
                <div style={{ fontSize:11, color:'#555', marginTop:3 }}>
                  ℹ️ Time is interpreted as <strong>{form.sendingTimezone}</strong> timezone
                </div>
              )}
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:700, color:'#ccc', marginBottom:6 }}>
                📩 Reply Notification Email <span style={{ color:'#555', fontWeight:400 }}>(optional)</span>
              </label>
              <input type="email" value={form.notificationEmail} onChange={e=>f('notificationEmail',e.target.value)} placeholder="e.g. sales@yourcompany.com"
                style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color: form.notificationEmail ? '#fff' : '#555', fontSize:13, outline:'none' }} />
              <div style={{ fontSize:11, color:'#555', marginTop:5 }}>💡 Replies will always go to your registered email. Add another email here to receive a copy.</div>
            </div>

            <div style={{ marginBottom:16, background:'rgba(46,204,113,0.05)', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, padding:'12px 16px' }}>
              <div style={{ fontSize:12, color:'#2ECC71', fontWeight:800, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Fixed Sending Settings</div>
              <div style={{ fontSize:13, color:'#ccc', display:'flex', flexDirection:'column', gap:4 }}>
                <span>⏱ Time between emails: <strong style={{ color:'#2ECC71' }}>2 minutes</strong></span>
                <span>🌐 Timezone: <strong style={{ color:'#2ECC71' }}>{FIXED_TIMEZONE}</strong></span>
                <span>📅 Sending days: <strong style={{ color:'#2ECC71' }}>{DAYS_LABEL}</strong></span>
              </div>
              <div style={{ fontSize:11, color:'#555', marginTop:6 }}>These are set platform-wide and can't be changed here.</div>
            </div>

            {/* Summary */}
            <div style={{ background:'#1a1a1a', borderRadius:12, padding:16, border:'1px solid rgba(46,204,113,0.12)' }}>
              <div style={{ fontSize:12, color:'#2ECC71', fontWeight:800, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Summary</div>
              {[
                ['Name', form.name],
                ['Subject', form.subject],
                ['Lists', `${form.leadListIds.length} list(s) — ${form.leadListIds.reduce((s,id)=>s+(lists.find(l=>l.id===id)?.count||0),0)} contacts`],
                ['Accounts', `${form.fromAccountIds.length} account(s)`],
                ['Daily Limit', `${form.dailyLimit} emails/day`],
                ['Timezone', form.sendingTimezone],
                ['Start Date', scheduledPreview ? `🕐 ${scheduledPreview}` : '⚡ Starts within 5 minutes'],
                ['Notify Email', form.notificationEmail || 'Registered email only'],
                ...(form.type==='ab_test'?[['A/B Split',`A: ${form.abSplitPercent||50}% / B: ${100-(form.abSplitPercent||50)}%`],['Winner By',form.abWinnerMetric?.replace('_',' ')||'open rate'],['Pick Winner',`After ${form.abWinnerAfterHours||4} hours`]]:[]),
              ].map(([l,v])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:13 }}>
                  <span style={{ color:'#666' }}>{l}</span>
                  <span style={{ color:'#ccc', maxWidth:280, textAlign:'right', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          {!editId && step>1&&<Button variant="secondary" onClick={()=>setStep(s=>s-1)} style={{ flex:1 }}>← Back</Button>}
          {!editId && step<4&&<Button onClick={()=>{ if(!canNext()){if(step===1&&!form.name)toast.error('Name required');if(step===2&&!form.subject)toast.error('Subject required');if(step===2&&!form.body)toast.error('Body required');if(step===3&&!form.leadListIds.length)toast.error('Select a contact list');if(step===3&&!form.fromAccountIds.length)toast.error('Select an account');return;}setStep(s=>s+1);}} style={{ flex:step>1?2:1 }}>Next →</Button>}
          {(step===4 || editId)&&<Button loading={saving} onClick={saveCampaign} style={{ flex:2 }}>🚀 {editId?'Update':'Create'} Campaign</Button>}
        </div>
      </Modal>

      {/* ── TEST EMAIL MODAL ── */}
      <Modal open={!!showTest} onClose={()=>setShowTest(null)} title="Send Test Email" size="sm">
        <p style={{ fontSize:13, color:'#888', marginBottom:16, lineHeight:1.7 }}>Send a test version of <strong style={{ color:'#fff' }}>{showTest?.name}</strong> to verify how it looks before launching.</p>
        <Input label="Send test to *" type="email" placeholder="your@email.com" value={testEmail} onChange={e=>setTestEmail(e.target.value)} />
        <Alert type="info" style={{ marginBottom:12 }}>Test emails are sent from your first connected account. Subject will be prefixed with [TEST].</Alert>
        <div style={{ display:'flex', gap:10 }}>
          <Button variant="secondary" onClick={()=>setShowTest(null)} style={{ flex:1 }}>Cancel</Button>
          <Button loading={testLoading} onClick={()=>sendTestEmail(showTest?.id)} style={{ flex:2 }}>📨 Send Test</Button>
        </div>
      </Modal>

      {/* ── SPAM CHECK MODAL ── */}
      <Modal open={!!showSpam} onClose={()=>{ setShowSpam(null); setSpamResult(null); }} title="Spam Score Check" size="md">
        {spamLoading ? <div style={{ textAlign:'center', padding:40, color:'#2ECC71' }}>🔍 Analyzing email...</div> : spamResult ? (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20, padding:20, background:'#1a1a1a', borderRadius:12, border:`1px solid ${spamResult.score===0?'rgba(46,204,113,0.3)':spamResult.score<=10?'rgba(243,156,18,0.3)':'rgba(231,76,60,0.3)'}` }}>
              <div style={{ fontSize:48, fontWeight:900, color:spamResult.score===0?'#2ECC71':spamResult.score<=10?'#F39C12':spamResult.score<=20?'#E67E22':'#E74C3C' }}>{spamResult.score}</div>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{spamResult.rating}</div>
                <div style={{ fontSize:13, color:'#888', marginTop:4 }}>{spamResult.recommendation}</div>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {spamResult.flags.map((flag,i) => (
                <div key={i} style={{ padding:'10px 14px', background:'#1a1a1a', borderRadius:8, border:`1px solid ${flag.startsWith('✅')?'rgba(46,204,113,0.15)':flag.startsWith('⚠️')?'rgba(243,156,18,0.15)':'rgba(231,76,60,0.15)'}`, fontSize:13, color:'#ccc', lineHeight:1.5 }}>{flag}</div>
              ))}
            </div>
            <Button fullWidth variant="secondary" onClick={()=>{ setShowSpam(null); setSpamResult(null); }} style={{ marginTop:16 }}>Close</Button>
          </div>
        ) : null}
      </Modal>

      {/* ── EMAIL PREVIEW MODAL ── */}
      <Modal open={!!showPreview} onClose={()=>setShowPreview(null)} title={`Preview: ${showPreview?.name}`} size="lg">
        {showPreview && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:16, justifyContent:'center' }}>
              {['desktop','mobile'].map(m => (
                <button key={m} onClick={()=>setPreviewMode(m)} style={{ padding:'7px 20px', borderRadius:8, border:`1px solid ${previewMode===m?'#2ECC71':'rgba(46,204,113,0.2)'}`, background:previewMode===m?'rgba(46,204,113,0.1)':'transparent', color:previewMode===m?'#2ECC71':'#666', fontSize:13, fontWeight:previewMode===m?700:400, cursor:'pointer' }}>
                  {m==='desktop'?'🖥 Desktop':'📱 Mobile'}
                </button>
              ))}
            </div>
            <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', margin:'0 auto', width:previewMode==='mobile'?375:'100%', transition:'width 0.3s', boxShadow:'0 4px 24px rgba(0,0,0,0.5)' }}>
              <div style={{ background:'#f5f5f5', padding:'12px 16px', borderBottom:'1px solid #ddd' }}>
                <div style={{ fontSize:12, color:'#333', fontWeight:700, marginBottom:4 }}>Subject: {showPreview.subject||'(No subject)'}</div>
                <div style={{ fontSize:11, color:'#888' }}>From: your@domain.com · To: lead@company.com</div>
              </div>
              <div style={{ padding:previewMode==='mobile'?'16px':'24px 32px', minHeight:300, background:'#fff' }}
                dangerouslySetInnerHTML={{ __html: showPreview.body
                  ? showPreview.body.replace(/\{\{first_name\}\}/gi,'John').replace(/\{\{last_name\}\}/gi,'Smith').replace(/\{\{company\}\}/gi,'Acme Inc').replace(/\{\{email\}\}/gi,'john@acme.com').replace(/\{\{job_title\}\}/gi,'CEO').replace(/\n/g,'<br>')
                  : '<p style="color:#999;font-style:italic;">No email body set</p>'
                }}
              />
              <div style={{ borderTop:'1px solid #eee', padding:'12px 24px', textAlign:'center', fontSize:11, color:'#aaa' }}>
                You received this email because you opted in. <a href="#" style={{ color:'#aaa' }}>Unsubscribe</a>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
